import React, { useState } from 'react';
import { Table } from 'react-bootstrap';
import { Formik } from 'formik';
import { useDispatch } from 'react-redux';
import snakecaseKeys from 'snakecase-keys';
import { createMassiveOvertimeRequest } from '../../../requests/overtimes';
import { sendAlert } from '../../../actions/utils';

const AuthorizeOvertimeFilterDatatable = ({ attendances, currentEmployee, onSuccess, employee }) => {
  const [onRequest, setOnRequest] = useState(false);
  const dispatch = useDispatch();
  const parseTimeToSeconds = t => {
    if (!t && t !== 0) return 0;
    const parts = String(t)
      .trim()
      .split(':')
      .map(Number);
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    if (!Number.isNaN(parts[0])) return parts[0];
    return 0;
  };

  const formatSecondsToHHMMSS = secs => {
    const s = Math.max(0, Math.floor(secs));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return [h, m, sec].map(n => String(n).padStart(2, '0')).join(':');
  };

  const getInitialValues = () => {
    const values = {};

    if (attendances && attendances.length) {
      attendances.forEach(attendance => {
        const safeKey = String(
          `${attendance.shift_id ?? ''}-${attendance.contract_id ?? ''}-${attendance.date ?? ''}`
        ).replace(/[^a-zA-Z0-9\-_]/g, '_');

        const totalSeconds =
          parseTimeToSeconds(attendance.extra_hours_begin) +
          parseTimeToSeconds(attendance.extra_hours_end) +
          parseTimeToSeconds(attendance.extra_hours_collation) +
          parseTimeToSeconds(attendance.extra_hours_out_shift);

        const totalFormatted = formatSecondsToHHMMSS(totalSeconds);
        values[safeKey] = {
          approved: false,
          status: 'approved',
          date_formatted: attendance.date_formatted,
          date: attendance.date,
          hours: 0,
          minutes: 0,
          employee_id: employee.employee_id,
          created_by_id: currentEmployee,
          hours_value: 'fifty',
          overtime_type: 'without_journey',
          activities: 'Aprobación por jefe directo',
          boss_supervisor_id: currentEmployee,
          contract_id: attendance.contract_id,
          shift_id: attendance?.shift_id,
          total_overtime: totalFormatted,
          original_total_seconds: totalSeconds,
          send_notification: false
        };
      });
    }

    return values;
  };

  const validateLength = overtimeValues => {
    if (!overtimeValues.overtimes.length) {
      dispatch(sendAlert({ kind: 'error', message: 'Debes seleccionar al menos un trabajador' }));
      return true;
    }
    return false;
  };

  const handleSuccessCreate = setSubmitting => {
    setSubmitting(false);
    const message = 'Horas Extras creadas con éxito';
    setOnRequest(false);
    dispatch(sendAlert({ kind: 'success', message }));
    if (typeof onSuccess === 'function') onSuccess();
  };

  const handleFailureRequest = error => {
    setOnRequest(false);
    dispatch(sendAlert({ kind: 'error', message: error.response?.data?.message }));
  };

  const handleCreate = (overtimeValues, setSubmitting) => {
    if (validateLength(overtimeValues)) {
      return;
    }
    setOnRequest(true);

    createMassiveOvertimeRequest({
      dispatch,
      params: snakecaseKeys(overtimeValues),
      formData: true,
      successCallback: () => handleSuccessCreate(setSubmitting),
      failureCallback: error => {
        handleFailureRequest(error);
        setSubmitting(false);
      }
    });
  };

  const handleSubmit = (values, { setSubmitting }) => {
    const submissions = Object.keys(values)
      .map(key => values[key])
      .filter(data => data && data.approved === true)
      .map(data => {
        // desglosar total_overtime en horas, minutos, segundos
        const parts = String(data.total_overtime || '00:00:00')
          .split(':')
          .map(p => parseInt(p, 10) || 0);
        let hours = 0;
        let minutes = 0;

        if (parts.length === 3) {
          [hours, minutes] = parts;
        } else if (parts.length === 2) {
          // formato "MM:SS"
          hours = 0;
          [minutes] = parts;
        } else if (parts.length === 1) {
          // solo un número -> lo tomamos como horas por defecto
          [hours] = parts;
          minutes = 0;
        }

        return {
          status: 'approved',
          date: data.date,
          hours,
          minutes,
          employee_id: data.employee_id,
          created_by_id: currentEmployee,
          hours_value: 'fifty',
          overtime_type: 'without_journey',
          activities: 'Aprobación por jefe directo',
          boss_supervisor_id: currentEmployee,
          contract_id: data.contract_id,
          shift_id: data?.shift_id,
          send_notification: false
        };
      });

    handleCreate({ overtimes: submissions }, setSubmitting);
    setSubmitting(false);
  };

  return (
    <Formik initialValues={getInitialValues()} onSubmit={handleSubmit}>
      {({ values, handleSubmit: formikHandleSubmit, setFieldValue, isSubmitting }) => {
        const renderResult = attendance => {
          const safeKey = String(
            `${attendance.shift_id ?? ''}-${attendance.contract_id ?? ''}-${attendance.date ?? ''}`
          ).replace(/[^a-zA-Z0-9\-_]/g, '_');
          const currentValues = values[safeKey] || {};
          const inputId = `total-overtime-${safeKey}`;

          return (
            <tr key={safeKey}>
              <td className="text-center align-middle">{attendance.date_formatted}</td>
              <td className="text-center align-middle">{attendance.shift}</td>
              <td className="text-center align-middle">{attendance.attendance_day}</td>
              <td className="text-center align-middle">{attendance.shift_break}</td>
              <td className="text-center align-middle">{attendance.attendance_break}</td>
              <td className="text-center align-middle">{attendance.extra_hours_begin}</td>
              <td className="text-center align-middle">{attendance.extra_hours_end}</td>
              <td className="text-center align-middle">{attendance.extra_hours_collation}</td>
              <td className="text-center align-middle">{attendance.extra_hours_out_shift}</td>
              <td className="text-center align-middle">
                <input
                  type="text"
                  id={inputId}
                  name={`${safeKey}.total_overtime`}
                  className="form-control form-control-sm text-center"
                  value={currentValues.total_overtime || '00:00:00'}
                  aria-label={`total-overtime-${attendance.date_formatted}`}
                  disabled={attendance.has_overtimes}
                  onChange={e => {
                    setFieldValue(`${safeKey}.total_overtime`, e.target.value);
                  }}
                  style={{ width: '80px', borderRadius: 5, padding: '0.25rem 0.4rem' }}
                />
              </td>
              <td className="text-center align-middle">
                {attendance.has_overtimes ? (
                  <span className="text-success">Autorizado</span>
                ) : (
                  <select
                    className="form-control form-control-sm"
                    value={currentValues.approved ? 'yes' : 'no'}
                    onChange={e => setFieldValue(`${safeKey}.approved`, e.target.value === 'yes')}
                    aria-label={`approve-${attendance.date_formatted}`}
                    disabled={currentValues.total_overtime === '00:00:00'}
                    style={{ borderRadius: 5, padding: '0.25rem 0.4rem' }}
                  >
                    <option value="no">No</option>
                    <option value="yes">Sí</option>
                  </select>
                )}
              </td>
            </tr>
          );
        };

        const renderData = () => {
          if (!attendances || attendances.length === 0) {
            return <p className="mt-2 mb-2">No hay datos para mostrar</p>;
          }

          const hasApproved = Object.keys(values || {}).some(key => values[key] && values[key].approved === true);

          return (
            <>
              <Table className="custom-data-table" bordered size="sm" style={{ fontWeight: 400, fontSize: '10px' }}>
                <thead className="rdt_TableHeadRow" style={{ backgroundColor: '#EBEBEB', textTransform: 'uppercase' }}>
                  <tr className="rdt_TableCol">
                    <th className="text-center align-middle">Fecha</th>
                    <th className="text-center align-middle">Jornada Pactada</th>
                    <th className="text-center align-middle">Marcas Jornada</th>
                    <th className="text-center align-middle">Colación</th>
                    <th className="text-center align-middle">Marcas Colación</th>
                    <th className="text-center align-middle">Al Inicio Jornada</th>
                    <th className="text-center align-middle">Al Término Jornada</th>
                    <th className="text-center align-middle">Por Colación</th>
                    <th className="text-center align-middle">Por Marcación sin Turno</th>
                    <th className="text-center align-middle">Total</th>
                    <th className="text-center align-middle">Autorizar</th>
                  </tr>
                </thead>
                <tbody>{attendances.map(attendance => renderResult(attendance))}</tbody>
              </Table>

              <div className="d-flex justify-content-end mt-3 mb-2">
                <button
                  type="button"
                  onClick={formikHandleSubmit}
                  className="btn btn-primary"
                  disabled={isSubmitting || !hasApproved || onRequest}
                  style={{ minWidth: '150px' }}
                >
                  {isSubmitting || onRequest ? 'Enviando...' : 'Enviar Aprobaciones'}
                </button>
              </div>
            </>
          );
        };

        return <>{renderData()}</>;
      }}
    </Formik>
  );
};

export default AuthorizeOvertimeFilterDatatable;
